import * as THREE from 'three';
import { scene, yawObject } from './scene.js';
import {
  buildBody, layoutBody, applyHands, animateLimbs, stepPhase, forwardSpeed,
} from './remote.js';
import { HELD_NADE, HELD_ADS, HELD_RELOAD, HELD_SPRINT, NADE_KIND } from './net.js';
import { WEAPON_ID } from './weapons.js';
import { player, S } from './state.js';

// ============================================================
//  Своё тело от первого лица: ноги, таз и собственная тень.
//
//  Зачем: раньше у своего игрока не было тела вообще — камера висела в
//  пустоте. Посмотрев вниз, игрок видел пол насквозь, а на стене рядом не
//  было даже тени. Это ломает ощущение присутствия сильнее, чем кажется:
//  человек постоянно видит край собственного тела, и его отсутствие читается
//  как «я летающая камера».
//
//  ⚠️ Тело собирается ТОЙ ЖЕ функцией, что тело чужого (`remote.js::buildBody`),
//  и анимируется теми же `layoutBody`/`applyHands`/`animateLimbs`. Копии кода
//  здесь нет намеренно: своя походка и походка, которую видит противник,
//  обязаны быть одним движением. Заведи вторую анимацию — и они разъедутся
//  при первой же правке, причём заметить это можно будет только с двух
//  компьютеров сразу.
//
//  ⚠️ Тело чисто визуальное и НИ НА ЧТО не влияет: сервер о нём не знает,
//  коллизии считаются по кругу вокруг глаз (`movement.rs`), рейкасты выстрела
//  идут по `wallMeshes` и по телам чужих. То есть застрелить себя в ногу
//  нельзя и упереться в собственный таз — тоже.
// ============================================================

/** Материал «не рисуй, но тень отбрасывай».
 *
 *  ⚠️ Не `visible = false` и не слои камеры, и это не каприз. Слои не подходят
 *  потому, что тень в three.js фильтруется по слоям ТОЙ ЖЕ камеры, что и
 *  картинка (`WebGLShadowMap.renderObject`): спрятав часть от глаза, мы
 *  спрятали бы её и от солнца. А `visible = false` теневой проход честно
 *  уважает (`result.visible = material.visible` там же). Материал же с
 *  `colorWrite:false` в теневой проход не копируется вовсе — в карту глубины
 *  часть попадает целиком.
 *
 *  ⚠️ `depthWrite:false` обязателен: с записью глубины невидимая грудь
 *  закрывала бы собой ноги за ней. */
const GHOST = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false });

function ghost(o) { if (o.isMesh) o.material = GHOST; }

// ⚠️ Третий аргумент — GHOST для оружия: мировые модели теперь GLB и грузятся
// асинхронно, подменяя примитив уже ПОСЛЕ разовой пряталки ниже
// (`own.hands.pivot.traverse(ghost)`). Без передачи материала внутрь
// загрузившийся GLB-ствол всплыл бы видимым поверх вьюмодели — «два ствола
// в руках» (баг-урок). Пряталка ниже остаётся ради гранат в руке и рук.
// ⚠️ Пятый аргумент `true` — «тело от первого лица». Человеческая модель тут
// та же, что у чужих, но с обрезанным набором материалов (`firstPersonKit`
// в `skinbody.js`): в кадре остаются ТОЛЬКО ноги, всё остальное режется
// в шейдере по разметке частей тела. Материалом это было не сделать — меш
// у модели один и материал на нём один, поэтому своё тело и оставалось
// коробочным дольше всех. Тень при этом целая: теневой проход рисует своим
// материалом глубины и патча не видит.
//
// ⚠️ Список `ghost(...)` ниже при этом НЕ лишний: он одевает КОРОБКИ, а они
// остаются рабочим откатом (`?body=boxes`) и видны те секунды, пока GLB летит
// по сети. Как только модель приехала, коробки гасятся целиком, и тень даёт
// уже она.
const own = buildBody(false, false, GHOST, true, true);
own.speed = 0;
own.phase = 0;
own.idle = 0;                    // часы покоя: дыхание и перенос веса
own.group.visible = false;       // покажет первый же кадр живого игрока
scene.add(own.group);

// ---- Что глаз видит, а что остаётся только тенью ----
//
// Видимы ТОЛЬКО НОГИ. Всё остальное — тень.
//
// ⚠️ Голова: камера сидит внутри её коробки (глаза −0.10…+0.12), и рисовать
// её значило бы смотреть на изнанку собственного черепа. Отсекается она сейчас
// отбраковкой задних граней сама, но полагаться на это нельзя: на переходе
// в позу лёжа камера на ~1 см вылезает выше коробки, и глаз ловил бы красную
// вспышку внизу экрана.
//
// ⚠️ Верх торса спрятан по РАСЧЁТУ, а не «на глаз». Его передне-верхнее ребро
// лежит в 0.18 м впереди и 0.10 м ниже глаз, то есть под 29° вниз от горизонта,
// а половина вертикального FOV — 37.5° (BASE_FOV = 75). Значит грудь торчала бы
// в нижних 14% экрана ВСЕГДА, а не только при взгляде вниз, — это уже не
// «вижу свои ноги», а перекрытая нижняя полоса экрана.
// Хочется видеть и грудь — убрать `upperTorso` из списка ниже, но помнить,
// что в прицеле (adsFov 50, половина 25°) она пропадает сама, а от бедра нет.
//
// ⚠️ Таз спрятан по ПРОСЬБЕ ВЛАДЕЛЬЦА («смотрю вниз при ходьбе — вижу жопу
// и ноги, а должен только ноги»), а не по расчёту: геометрически он вскрывался
// честно, примерно с 35° взгляда вниз. Просто своя пятая точка в кадре — это
// вид от третьего лица, а не от первого: смысл тела здесь в ногах и в тени.
// ⚠️ Тень при этом остаётся ЦЕЛОЙ — таз, как и грудь, уходит в `GHOST`,
// то есть перестаёт рисоваться, но продолжает попадать в карту глубины.
// Вернуть таз в кадр — убрать `lowerTorso` из списка ниже.
//
// ⚠️ Руки и предмет в них спрятаны потому, что оружие игроку уже показывает
// вьюмодель (`weapons.js`), приклеенная к камере. Оставь и то и другое —
// в кадре окажутся ДВА ствола сразу. Тем же путём идёт CS: от первого лица
// у тела нет рук, они есть только у вьюмодели.
ghost(own.parts.head);
ghost(own.parts.upperTorso);
ghost(own.parts.lowerTorso);
own.limbs.armL.root.traverse(ghost);
own.limbs.armR.root.traverse(ghost);
own.hands.pivot.traverse(ghost);

/** Байт «что в руках» из локального состояния — раскладка в `net.js`.
 *
 *  Своего снапшота клиент не получает (сервер вырезает получателя), поэтому
 *  собираем сами. Видно это только в тени, так что доставание (`HELD_DRAW`)
 *  здесь намеренно отсутствует: его длительность знает сервер
 *  (`weapons::DRAW_TIME`), и заводить на клиенте копию ради тени — та самая
 *  третья копия баланса, о которой предупреждает `remote.js`. */
function heldByte() {
  if (S.nadeMode) return HELD_NADE | NADE_KIND[S.nadeMode];
  let b = WEAPON_ID[S.curKey];
  // Бег перекрывает прицеливание — тем же правилом и в том же порядке, что
  // на сервере (`room.rs::held_byte`): на бегу оружие прижато к корпусу.
  if (S.sprinting) b |= HELD_SPRINT;
  else if (S.aiming) b |= HELD_ADS;
  if (S.reloading) b |= HELD_RELOAD;
  return b;
}

/**
 * Кадр своего тела: поставить, разложить по позе, прошагать ногами.
 *
 * ⚠️ Позиция берётся из `yawObject`, а НЕ из `player.pos`: в риге головы уже
 * лежит `pos + predError`, то есть сглаженный остаток серверной коррекции.
 * Возьми чистую позицию — и тело поехало бы отдельно от камеры на каждом
 * откате reconciliation, что выглядит как «ноги отстают от меня».
 *
 * ⚠️ Наклон Q/E передаём `S.leanShift` (обрезанный стеной), а не `S.lean`
 * (намерение) — по той же причине, по которой по нему едет камера: тело
 * обязано стоять там, откуда сервер считает видимость и выстрел.
 */
export function updateOwnBody(dt) {
  const g = own.group;
  g.visible = player.alive;
  if (!player.alive) return;

  // Группа стоит СТУПНЯМИ на полу — как у чужих (`updateRemotes`), потому что
  // от ног её раскладывает `layoutBody`. В прыжке ступни честно отрываются:
  // `player.pos.y` уходит вверх, а высота глаз не меняется.
  g.position.copy(yawObject.position);
  g.position.y -= player.eye;
  g.rotation.y = S.yaw;

  // Поза — из ФАКТИЧЕСКОЙ высоты глаз, а не из тумблеров приседа/лёжа: глаза
  // опускаются плавно (`stepPhysics`), и по флагу тело складывалось бы рывком
  // раньше камеры. Ровно то же правило, что у чужих и у хитбоксов сервера.
  layoutBody(own, player.eye, S.leanShift, S.pitch);

  // Фаза шага — из пройденного пути (см. STEP_DIST), поэтому нога касается
  // пола в такт слышимому шагу. Скорость берём прямо из вектора скорости:
  // она у своего игрока известна точно, оценивать её по разнице позиций
  // (как у чужих) не нужно и не надо — там это вынужденная мера.
  //
  // ⚠️ И направление — оттуда же: пятящийся отматывает цикл шага назад, а не
  // шагает вперёд задом наперёд (`stepPhase`). Своему это видно НАПРЯМУЮ:
  // отступая от угла, игрок смотрит себе под ноги чаще всего.
  const speed = Math.hypot(player.vel.x, player.vel.z);
  own.speed = speed;
  own.idle += dt;
  stepPhase(own, speed * dt, forwardSpeed(player.vel.x, player.vel.z, S.yaw));

  // Оружие ставим ДО рук: `animateLimbs` доводит кисти к уже посчитанным
  // точкам хвата. Видно это только в тени — но тень с оружием в руках и есть
  // половина смысла всей затеи.
  applyHands(own, heldByte(), player.eye, S.leanShift, S.pitch, dt);
  animateLimbs(own, S.pitch);
}
